// Raw edge-function calls for the desk. Unlike callEdgeFunction() this never
// throws for an HTTP status and reports a missing response as status 0, so
// outcomes.ts can tell "the server said no" from "we never reached it".

import { FunctionsFetchError, FunctionsHttpError, FunctionsRelayError, type SupabaseClient } from '@supabase/supabase-js';
import type { RawResponse } from './outcomes';

export type DeskFunctionName = 'redeem-booking' | 'host-balance';

export type DeskInvoke = (name: DeskFunctionName, body: Record<string, unknown>) => Promise<RawResponse>;

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    const parsed: unknown = await response.json();
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function createDeskInvoke(supabase: SupabaseClient): DeskInvoke {
  return async (name, body) => {
    let result: Awaited<ReturnType<SupabaseClient['functions']['invoke']>>;
    try {
      result = await supabase.functions.invoke(name, { body });
    } catch {
      return { status: 0, body: {} };
    }
    const { data, error } = result;
    if (!error) {
      return { status: 200, body: data && typeof data === 'object' ? (data as Record<string, unknown>) : {} };
    }
    if (error instanceof FunctionsHttpError) {
      const response = error.context as Response;
      return { status: response.status, body: await readJson(response) };
    }
    if (error instanceof FunctionsRelayError) {
      // The Supabase relay failed before our function answered — nothing was
      // decided, treat like a 5xx.
      return { status: 502, body: {} };
    }
    if (error instanceof FunctionsFetchError) return { status: 0, body: {} };
    return { status: 0, body: {} };
  };
}
